import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Loader2,
    FlaskConical,
    Trophy,
    PauseCircle,
    PlayCircle,
    CheckCircle2,
    StickyNote,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminExperimentDetail,
    adminExperimentPatch,
    adminExperimentAddNote,
} from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";

const CONFIDENCE_PALETTE = {
    "99%": "text-[var(--bl-moss)]",
    "95%": "text-[var(--bl-moss)]",
    "90%": "text-[var(--bl-amber)]",
};

function StatRow({ stats, primaryMetric, winningId }) {
    const maxVisitors = Math.max(1, ...stats.map((s) => s.visitors));
    return (
        <div
            className="bl-card overflow-x-auto"
            data-testid="experiment-variants-table"
        >
            <table className="w-full text-sm">
                <thead>
                    <tr>
                        {[
                            "Variant",
                            "Visitors",
                            "Conversions",
                            "Rate",
                            "Lift",
                            "Confidence",
                            "Revenue",
                            "",
                        ].map((h) => (
                            <th
                                key={h}
                                className="px-4 py-3 text-left text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {stats.map((s) => (
                        <tr
                            key={s.variant_id}
                            className="border-b border-bl/40 last:border-b-0"
                            data-testid={`variant-row-${s.variant_id}`}
                        >
                            <td className="px-4 py-3 text-bl">
                                <div className="font-medium">{s.name}</div>
                                <div className="text-xs text-bl-muted flex items-center gap-1.5 mt-0.5">
                                    {s.is_control && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bl-stone-soft)]">
                                            control
                                        </span>
                                    )}
                                    {winningId === s.variant_id && (
                                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-[var(--bl-moss-tint)] text-[var(--bl-moss)] inline-flex items-center gap-1">
                                            <Trophy className="h-2.5 w-2.5" />
                                            winner
                                        </span>
                                    )}
                                </div>
                            </td>
                            <td className="px-4 py-3 font-mono">
                                <div className="flex items-center gap-2">
                                    <span>{s.visitors}</span>
                                    <div className="h-1.5 bg-[var(--bl-stone-soft)] rounded overflow-hidden w-20">
                                        <div
                                            className="h-full bg-bl-primary"
                                            style={{
                                                width: `${(s.visitors / maxVisitors) * 100}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            </td>
                            <td className="px-4 py-3 font-mono">
                                {s.conversions}
                            </td>
                            <td className="px-4 py-3 font-mono">
                                {s.conversion_rate.toFixed(2)}%
                            </td>
                            <td className="px-4 py-3 font-mono">
                                {s.is_control ? (
                                    <span className="text-bl-muted">—</span>
                                ) : s.lift_pct == null ? (
                                    <span className="text-bl-muted">n/a</span>
                                ) : (
                                    <span
                                        className={
                                            s.lift_pct >= 0
                                                ? "text-[var(--bl-moss)]"
                                                : "text-[var(--bl-rust)]"
                                        }
                                    >
                                        {s.lift_pct >= 0 ? "+" : ""}
                                        {s.lift_pct}%
                                    </span>
                                )}
                            </td>
                            <td
                                className={`px-4 py-3 font-mono ${
                                    CONFIDENCE_PALETTE[s.confidence_level] ||
                                    "text-bl-muted"
                                }`}
                            >
                                {s.confidence_level || (s.is_control ? "—" : "n/a")}
                            </td>
                            <td className="px-4 py-3 font-mono">
                                {s.revenue_usd > 0
                                    ? `$${s.revenue_usd.toLocaleString()}`
                                    : "—"}
                            </td>
                            <td className="px-4 py-3 text-right">
                                {/* "Mark winner" trigger handled by parent */}
                                {!s.is_control && (
                                    <button
                                        type="button"
                                        data-action="mark-winner"
                                        data-variant-id={s.variant_id}
                                        className="text-xs text-bl-primary hover:underline underline-offset-4"
                                        data-testid={`mark-winner-${s.variant_id}`}
                                    >
                                        Mark winner
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="px-4 py-3 text-xs text-bl-muted border-t border-bl">
                Primary metric: <span className="font-mono">{primaryMetric}</span> ·
                Statistical confidence requires n ≥ 30 per variant (two-proportion z-test, two-tailed).
            </div>
        </div>
    );
}

function FunnelView({ stages }) {
    if (!stages?.length) return null;
    const top = Math.max(1, ...stages.map((s) => s.users));
    return (
        <div className="bl-card p-5" data-testid="experiment-funnel">
            <h3 className="font-display font-semibold text-base text-bl mb-3">
                Conversion funnel (all variants)
            </h3>
            <div className="space-y-2">
                {stages.map((s, i) => {
                    const prev = i > 0 ? stages[i - 1].users : null;
                    const stepRate =
                        prev && prev > 0 ? Math.round((s.users / prev) * 100) : null;
                    return (
                        <div
                            key={s.stage}
                            className="flex items-center gap-3"
                            data-testid={`funnel-stage-${s.stage}`}
                        >
                            <div className="w-44 text-xs text-bl-muted capitalize">
                                {s.stage.replace(/_/g, " ")}
                            </div>
                            <div className="flex-1">
                                <div className="h-6 bg-[var(--bl-stone-soft)] rounded overflow-hidden">
                                    <div
                                        className="h-full bg-bl-primary"
                                        style={{
                                            width: `${(s.users / top) * 100}%`,
                                        }}
                                    />
                                </div>
                            </div>
                            <div className="w-20 text-right font-mono text-xs text-bl">
                                {s.users}
                            </div>
                            <div className="w-12 text-right font-mono text-xs text-bl-muted">
                                {stepRate != null ? `${stepRate}%` : ""}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function AdminExperimentDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newNote, setNewNote] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        try {
            const r = await adminExperimentDetail(id);
            setData(r);
        } catch (e) {
            toast.error("Could not load experiment.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const patch = async (body, friendly) => {
        if (busy) return;
        setBusy(true);
        try {
            await adminExperimentPatch(id, body);
            toast.success(friendly);
            await refresh();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Update failed.",
            );
        } finally {
            setBusy(false);
        }
    };

    const onMarkWinner = (variantId) => {
        patch(
            { winning_variant_id: variantId, status: "completed" },
            "Winner marked. Experiment moved to completed.",
        );
    };

    const onAddNote = async () => {
        const n = newNote.trim();
        if (!n || busy) return;
        setBusy(true);
        try {
            await adminExperimentAddNote(id, n);
            setNewNote("");
            await refresh();
            toast.success("Note added.");
        } catch (e) {
            toast.error("Could not add note.");
        } finally {
            setBusy(false);
        }
    };

    if (loading)
        return (
            <PublicLayout>
                <section className="bl-section py-10">
                    <div className="bl-container">
                        <Loader2 className="h-4 w-4 animate-spin inline" />
                    </div>
                </section>
            </PublicLayout>
        );

    if (!data) return null;
    const exp = data.experiment;
    return (
        <PublicLayout>
            <section
                className="bl-section py-10 sm:py-14"
                data-testid="admin-experiment-detail-page"
                onClick={(e) => {
                    // Delegate "Mark winner" clicks (since the button lives in StatRow)
                    if (e.target?.dataset?.action === "mark-winner") {
                        onMarkWinner(e.target.dataset.variantId);
                    }
                }}
            >
                <div className="bl-container max-w-5xl">
                    <Link
                        to="/admin/experiments"
                        className="text-sm text-bl-muted hover:text-bl underline-offset-4 hover:underline"
                    >
                        ← All experiments
                    </Link>

                    <div className="flex items-start justify-between gap-3 flex-wrap mt-3 mb-6">
                        <div>
                            <div className="flex items-center gap-2">
                                <FlaskConical className="h-5 w-5 text-bl-primary" />
                                <h1
                                    className="font-display font-semibold text-3xl tracking-tight text-bl"
                                    data-testid="experiment-name"
                                >
                                    {exp.name}
                                </h1>
                            </div>
                            {exp.hypothesis && (
                                <p className="mt-2 text-sm text-bl-muted max-w-2xl leading-relaxed">
                                    <span className="italic">Hypothesis:</span>{" "}
                                    {exp.hypothesis}
                                </p>
                            )}
                            <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-bl-muted">
                                <span className="font-mono">{exp.location}</span>
                                <span>·</span>
                                <span>Status: <span className="font-semibold">{exp.status}</span></span>
                                {exp.start_date && (
                                    <>
                                        <span>·</span>
                                        <span>Started {exp.start_date.slice(0, 10)}</span>
                                    </>
                                )}
                                {exp.end_date && (
                                    <>
                                        <span>·</span>
                                        <span>Ended {exp.end_date.slice(0, 10)}</span>
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center gap-2" data-testid="experiment-actions">
                            {exp.status === "running" && (
                                <button
                                    onClick={() => patch({ status: "paused" }, "Paused.")}
                                    disabled={busy}
                                    className="bl-btn-secondary gap-2 text-sm"
                                    data-testid="exp-action-pause"
                                >
                                    <PauseCircle className="h-3.5 w-3.5" /> Pause
                                </button>
                            )}
                            {exp.status === "paused" && (
                                <button
                                    onClick={() => patch({ status: "running" }, "Resumed.")}
                                    disabled={busy}
                                    className="bl-btn-secondary gap-2 text-sm"
                                    data-testid="exp-action-resume"
                                >
                                    <PlayCircle className="h-3.5 w-3.5" /> Resume
                                </button>
                            )}
                            {["running", "paused"].includes(exp.status) && (
                                <button
                                    onClick={() => patch({ status: "completed" }, "Completed.")}
                                    disabled={busy}
                                    className="bl-btn-secondary gap-2 text-sm"
                                    data-testid="exp-action-complete"
                                >
                                    <CheckCircle2 className="h-3.5 w-3.5" /> Complete
                                </button>
                            )}
                            {exp.status === "draft" && (
                                <button
                                    onClick={() => patch({ status: "running" }, "Started.")}
                                    disabled={busy}
                                    className="bl-btn-primary gap-2 text-sm"
                                    data-testid="exp-action-start"
                                >
                                    <PlayCircle className="h-3.5 w-3.5" /> Start
                                </button>
                            )}
                            {exp.status === "completed" && (
                                <button
                                    onClick={() => patch({ status: "archived" }, "Archived.")}
                                    disabled={busy}
                                    className="bl-btn-secondary gap-2 text-sm"
                                    data-testid="exp-action-archive"
                                >
                                    Archive
                                </button>
                            )}
                        </div>
                    </div>

                    <h2 className="text-xs uppercase tracking-[0.2em] text-bl-muted mb-2">
                        Primary metric: {data.primary_metric}
                    </h2>
                    <StatRow
                        stats={data.per_variant}
                        primaryMetric={data.primary_metric}
                        winningId={exp.winning_variant_id}
                    />

                    {/* Secondary metrics */}
                    {Object.keys(data.secondary || {}).length > 0 && (
                        <div className="mt-8">
                            <h2 className="text-xs uppercase tracking-[0.2em] text-bl-muted mb-2">
                                Secondary metrics
                            </h2>
                            <div className="space-y-4">
                                {Object.entries(data.secondary).map(([metric, stats]) => (
                                    <div key={metric}>
                                        <div className="text-sm text-bl-muted mb-1.5">
                                            {metric}
                                        </div>
                                        <StatRow
                                            stats={stats}
                                            primaryMetric={metric}
                                            winningId={null}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Funnel */}
                    <div className="mt-8">
                        <FunnelView stages={data.funnel} />
                    </div>

                    {/* Notes */}
                    <div className="mt-8" data-testid="experiment-notes">
                        <div className="flex items-center gap-2 mb-2">
                            <StickyNote className="h-4 w-4 text-bl-muted" />
                            <h2 className="text-xs uppercase tracking-[0.2em] text-bl-muted">
                                Admin notes
                            </h2>
                        </div>
                        <div className="bl-card p-4">
                            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                                <input
                                    type="text"
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    placeholder="Add a note about this experiment…"
                                    className="bl-input flex-1"
                                    data-testid="experiment-note-input"
                                />
                                <button
                                    onClick={onAddNote}
                                    disabled={busy || !newNote.trim()}
                                    className="bl-btn-primary text-sm"
                                    data-testid="experiment-note-submit"
                                >
                                    Add note
                                </button>
                            </div>
                            {data.notes?.length === 0 ? (
                                <p className="text-sm text-bl-muted">
                                    No notes yet.
                                </p>
                            ) : (
                                <ul className="space-y-2.5">
                                    {data.notes.map((n) => (
                                        <li
                                            key={n.note_id}
                                            className="text-sm text-bl"
                                        >
                                            <div className="text-xs text-bl-muted">
                                                {n.admin_email} ·{" "}
                                                {n.created_at?.slice(0, 16).replace("T", " ")}
                                            </div>
                                            <div className="mt-0.5">{n.note}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
}
